import { useMemo, useState } from 'react';

import { useGetFxExchangeRate } from 'api';

import { useOrganisationId } from './context';

type UseFxRateArgs = {
  payCurrencyCode?: string | null;
  buyCurrencyCode?: string | null;
};

export type NormalizedFx = {
  payCurrency: string;
  buyCurrency: string;
  utcTime: string;
  exchangeRate: number; // 1 BUY = X PAY (pay_amount = buy_amount * exchangeRate)
  reciprocalExchangeRate: number; // 1 PAY = Y BUY
};

export function useFxRate({ payCurrencyCode, buyCurrencyCode }: UseFxRateArgs) {
  const organisationId = useOrganisationId<number>();

  const {
    data: { data: fx } = {},
    isSuccess,
    isError,
    isFetching,
    error
  } = useGetFxExchangeRate(
    organisationId,
    {
      buy_currency: buyCurrencyCode!,
      pay_currency: payCurrencyCode!,
      fixed_side: 'mid'
    },
    {
      query: {
        enabled: Boolean(payCurrencyCode && buyCurrencyCode)
      },
      request: {
        validateStatus: (status) => {
          if (status === 200) {
            return true;
          }
          // anything else than 200 is not valid
          if (status === 404) {
            setRateNotFound(true);
          }
          return false;
        }
      }
    }
  );

  const [rateNotFound, setRateNotFound] = useState(false);

  const normalized: NormalizedFx | undefined = useMemo(() => {
    if (!fx) return undefined;

    return {
      payCurrency: fx.pay_currency ?? '',
      buyCurrency: fx.buy_currency ?? '',
      exchangeRate: Number(fx.exchange_rate ?? 0),
      reciprocalExchangeRate: Number(fx.reciprocal_exchange_rate ?? 0),
      utcTime: fx.utc_time ?? ''
    };
  }, [fx]);

  const hasError = isError && !isFetching;
  const loading = isFetching && !normalized;
  const generalError = hasError && !rateNotFound;

  return {
    fx: normalized,
    loading,
    success: isSuccess,
    error: generalError,
    rawError: error,
    rateUnavailable: rateNotFound
  };
}
